import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RootState } from 'src/redux/rootReducerType';
import { setSavedSignature } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import { addAlert } from 'src/redux/slices/Compliance/CertificationAlertsSlice';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import axios from 'src/utils/axios';
import { parseXfdfPreview } from './xfdfPreview';
import SignaturePadModal from './SignaturePadModal';
import type { SignatureResult } from './SignaturePadModal';
import {
  buildStandaloneInkXfdf,
  buildStandaloneFreetextXfdf,
} from './xfdfSerializer';

interface SavedSignatureCardProps {
  isImpersonated: boolean;
}

function XfdfInkPreview({
  viewBox,
  paths,
}: {
  viewBox: string;
  paths: string[];
}): ReactNode {
  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%' }}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

const SavedSignatureCard: FC<SavedSignatureCardProps> = ({
  isImpersonated,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const iconColor =
    theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary';
  const signatureXfdf = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedSignatureXfdf
  );
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  const userName =
    useSelector(
      (state: RootState) => state.newRenterInterfaceUser.user?.displayName
    ) ?? '';

  const preview = useMemo(
    () => (signatureXfdf ? parseXfdfPreview(signatureXfdf) : null),
    [signatureXfdf]
  );

  const handleViewOpen = useCallback(() => {
    setViewOpen(true);
  }, []);
  const handleViewClose = useCallback(() => {
    setViewOpen(false);
  }, []);
  const handleEditOpen = useCallback(() => {
    setCreatorOpen(true);
  }, []);
  const handleCreatorClose = useCallback(() => {
    setCreatorOpen(false);
  }, []);
  const handleDeleteOpen = useCallback(() => {
    setDeleteOpen(true);
  }, []);
  const handleDeleteClose = useCallback(() => {
    setDeleteOpen(false);
  }, []);
  const handleAddOpen = useCallback(() => {
    setCreatorOpen(true);
  }, []);

  const handleModalDone = useCallback(
    async (result: SignatureResult) => {
      setCreatorOpen(false);
      setSaving(true);
      try {
        let xfdf: string;
        if (result.type === 'ink') {
          xfdf = buildStandaloneInkXfdf(result.strokes, userName);
        } else {
          xfdf = buildStandaloneFreetextXfdf(
            result.text,
            result.fontFamily,
            userName
          );
        }

        await axios.put('signature/saved_signature/', {
          signature_data: xfdf,
        });
        dispatch(setSavedSignature(xfdf));
      } catch {
        dispatch(
          addAlert({
            context: 'renter',
            alert: {
              type: 'general',
              severity: 'error',
              message: 'Failed to save signature. Please try again.',
              timeout: 5000,
            },
          })
        );
      } finally {
        setSaving(false);
      }
    },
    [userName, dispatch]
  );

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await axios.delete('signature/saved_signature/');
      dispatch(setSavedSignature(null));
    } catch {
      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'error',
            message: 'Failed to delete signature. Please try again.',
            timeout: 5000,
          },
        })
      );
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }, [dispatch]);

  const hasSignature = !!signatureXfdf && !!preview;

  const renderContent = (): ReactNode => {
    if (saving) {
      return (
        <>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Saving…
          </Typography>
        </>
      );
    }

    if (hasSignature) {
      return (
        <>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckCircleIcon
              sx={(theme) => ({
                fontSize: 16,
                color:
                  theme.palette.mode === 'dark'
                    ? 'primary.light'
                    : 'primary.main',
              })}
            />
            <Typography
              variant="body2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? 'primary.light'
                    : 'primary.main',
              })}
              fontWeight={500}
            >
              Saved signature
            </Typography>
          </Stack>
          {!isImpersonated && (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="View">
                <IconButton size="small" onClick={handleViewOpen}>
                  <GoogleFontIcon
                    name="visibility"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={handleEditOpen}>
                  <GoogleFontIcon
                    name="edit"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={handleDeleteOpen}>
                  <GoogleFontIcon
                    name="delete"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </>
      );
    }

    return (
      <>
        <Typography variant="body2" color="text.secondary">
          Add signature
        </Typography>
        {!isImpersonated && (
          <Tooltip title="Add signature">
            <IconButton size="small" onClick={handleAddOpen}>
              <GoogleFontIcon
                name="add"
                sx={{ fontSize: 18, color: iconColor }}
              />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flex: 1,
          border: 1,
          borderColor: 'grey.500',
          borderRadius: 2,
          backgroundColor: 'background.paper',
          px: 1.5,
          py: 1.5,
        }}
      >
        {renderContent()}
      </Box>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="xs" fullWidth>
        <DialogTitle>Saved Signature</DialogTitle>
        <DialogContent>
          {preview && (
            <Box
              sx={{
                width: '100%',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                color: 'text.primary',
              }}
            >
              {preview.type === 'ink' ? (
                <XfdfInkPreview
                  viewBox={preview.viewBox}
                  paths={preview.paths}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: preview.fontFamily,
                    fontSize: '2rem',
                  }}
                >
                  {preview.text}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth="xs">
        <DialogTitle>Delete Saved Signature</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your saved signature? You will need
            to create a new one next time you sign.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Creator Modal (Add/Edit) */}
      <SignaturePadModal
        open={creatorOpen}
        onClose={handleCreatorClose}
        onDone={handleModalDone}
        userName={userName}
        hideCheckbox={true}
        initialTab={preview?.type === 'text' ? 1 : 0}
      />
    </>
  );
};

export default SavedSignatureCard;
